import type { PartnerUserSummary } from "@redotech/redo-partners-portal-rpc/schema/shared/partner-user";
import { ClientError } from "@redotech/rpc/errors";
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";

import { usePartnersPortalRpcClient } from "../rpc/client-context";
import { useAuthToken } from "./auth-token-context";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthDataContextValue {
  status: AuthStatus;
  partnerUser: PartnerUserSummary | undefined;
}

const AuthDataContext = createContext<AuthDataContextValue | undefined>(
  undefined,
);

// Pages that mustn't call getMe: verify is mid-sign-in, login is signed out.
const PUBLIC_PATHS = new Set(["/login", "/auth/verify"]);

function isSessionRejected(error: unknown): boolean {
  const code = error instanceof ClientError ? error.statusCode : undefined;
  return code === 401 || code === 403;
}

export function AuthDataProvider({ children }: { children: ReactNode }) {
  const { token, clear } = useAuthToken();
  const rpcClient = usePartnersPortalRpcClient();
  const onPublicPath = PUBLIC_PATHS.has(useLocation().pathname);

  const query = useQuery({
    queryKey: ["me", token],
    queryFn: async () => (await rpcClient.getMe({})).partnerUser,
    enabled: !!token && !onPublicPath,
    // A rejected session won't recover; a server error might.
    retry: (failureCount, error) =>
      failureCount < 2 && !isSessionRejected(error),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (isSessionRejected(query.error)) {
      clear();
    }
  }, [query.error, clear]);

  // A server error stays "loading" rather than signing the user out.
  const status: AuthStatus =
    !token || onPublicPath || isSessionRejected(query.error)
      ? "unauthenticated"
      : query.data
        ? "authenticated"
        : "loading";

  const value = useMemo(
    () => ({ status, partnerUser: query.data }),
    [status, query.data],
  );
  return (
    <AuthDataContext.Provider value={value}>
      {children}
    </AuthDataContext.Provider>
  );
}

export function useAuthData(): AuthDataContextValue {
  const ctx = useContext(AuthDataContext);
  if (!ctx) {
    throw new Error("useAuthData must be used within AuthDataProvider");
  }
  return ctx;
}
